import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Briefcase, User } from "lucide-react";
import { IMAGES } from "@/data/products";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/Primitives";

const PATHS = [
  {
    id: "personal",
    icon: User,
    kicker: "Personal",
    title: "Track Your Vehicles & Valuables",
    items: ["Cars", "Trailers", "Motorbikes", "Luggage", "Bikes", "Tools", "Outdoor gear"],
    cta: "Shop Personal",
    to: "/shop",
    img: IMAGES.carBoot,
  },
  {
    id: "business",
    icon: Briefcase,
    kicker: "Business",
    title: "Track Your Fleet & Equipment",
    items: ["Work vehicles", "Trailers", "Tools", "Equipment", "Business assets", "Small fleets"],
    cta: "Explore Fleet",
    to: "/fleet",
    img: IMAGES.fleetVans,
    dark: true,
  },
];

const Pathways = () => (
  <section data-testid="pathways-section" className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
    <div className="mx-auto max-w-7xl">
      <SectionHeader
        eyebrow="Magnetic tracking for the things that move"
        title="Vehicles. Trailers. Fleets. Equipment. Everyday essentials."
        desc="One compact magnetic tag, two ways to buy. Pick the path that fits you."
        className="mb-10"
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {PATHS.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <Link
              to={p.to}
              data-testid={`pathway-${p.id}`}
              className={`group relative flex min-h-[420px] flex-col justify-end overflow-hidden rounded-[2rem] shadow-lift ${p.dark ? "bg-tracksy-dark" : "bg-tracksy-cream"}`}
            >
              <img
                src={p.img}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className={`absolute inset-0 ${p.dark ? "bg-gradient-to-t from-tracksy-dark via-tracksy-dark/70 to-tracksy-dark/10" : "bg-gradient-to-t from-black/80 via-black/40 to-transparent"}`} />
              <div className="relative p-7 sm:p-9">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-widest text-white backdrop-blur-xl">
                  <p.icon className="h-3.5 w-3.5 text-tracksy-orange" />
                  {p.kicker}
                </span>
                <h3 className="mt-4 font-heading text-2xl font-bold text-white sm:text-3xl">{p.title}</h3>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {p.items.map((it) => (
                    <li key={it} className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/85">{it}</li>
                  ))}
                </ul>
                <motion.span className="mt-6 inline-flex items-center gap-2 rounded-full bg-tracksy-orange px-6 py-3 text-xs font-bold uppercase tracking-wide text-white">
                  {p.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </motion.span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Pathways;
